// Smart Login (Legacy Accounts) - Auto-Activation on Submit
// Intercepts login form submission to pre-check customer account state
// and automatically send activation emails for inactive accounts

(function () {
  'use strict';

  // Configuration
  const PRECHECK_API_URL = 'https://texasartsupply-smart-login.vercel.app/api/auth/precheck';
  const DEBUG = window.location.search.includes('debug=1');

  console.log('🔐 Smart Login initialized');
  if (DEBUG) {
    console.log('📍 API URL:', PRECHECK_API_URL);
    console.log('🐛 Debug mode: ENABLED');
  }

  /**
   * Create Storefront API customer access token and store it.
   * Required so the account page doesn't redirect to login (it checks this token).
   * Call this before form.submit() when doing PASS_THROUGH.
   */
  async function createAndStoreStorefrontToken(email, password) {
    const config = window.SMART_LOGIN_STOREFRONT;
    if (!config || !config.apiUrl || !config.accessToken) {
      if (DEBUG) console.log('⚠️ No Storefront config - account page may redirect to login');
      return;
    }
    try {
      const response = await fetch(config.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': config.accessToken,
        },
        body: JSON.stringify({
          query: 'mutation customerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) { customerAccessTokenCreate(input: $input) { customerAccessToken { accessToken expiresAt } customerUserErrors { field message } } }',
          variables: { input: { email: email, password: password } },
        }),
      });
      const data = await response.json();
      const result = data.data && data.data.customerAccessTokenCreate;
      if (!result || !result.customerAccessToken) {
        if (DEBUG && result && result.customerUserErrors) {
          console.warn('Storefront token create errors:', result.customerUserErrors);
        }
        return;
      }
      const token = result.customerAccessToken;
      localStorage.setItem('customerAccessToken', token.accessToken);
      localStorage.setItem('customerAccessTokenExpiresAt', token.expiresAt);
      var maxAge = 7 * 24 * 60 * 60;
      document.cookie = 'customerAccessToken=' + token.accessToken + '; max-age=' + maxAge + '; path=/; secure; samesite=strict';
      if (DEBUG) console.log('✅ Storefront token stored (prevents account page redirect)');
    } catch (err) {
      if (DEBUG) console.warn('Could not create Storefront token:', err);
    }
  }

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    // Find all login forms (both popup and page)
    const loginForms = document.querySelectorAll(
      'form[action*="account/login"], ' +
      'form[action*="/account/login"], ' +
      '.auth-form, ' +
      '.bls-login form'
    );

    if (DEBUG) {
      console.log('🔍 Found login forms:', loginForms.length);
    }

    loginForms.forEach((form) => {
      // Check if this form is a login form (not register or recover)
      const formType = form.querySelector('input[name="form_type"]');
      if (formType && formType.value !== 'customer_login') {
        if (DEBUG) console.log('⏭️ Skipping non-login form');
        return;
      }

      // Check if form has both email and password fields
      const emailInput = 
        form.querySelector('input[name="customer[email]"]') ||
        form.querySelector('input[type="email"]') ||
        form.querySelector('#customer_email');
      
      const passwordInput = 
        form.querySelector('input[name="customer[password]"]') ||
        form.querySelector('input[type="password"]') ||
        form.querySelector('#customer_password');

      if (!emailInput || !passwordInput) {
        if (DEBUG) console.log('⚠️ Form missing email or password input');
        return;
      }

      if (DEBUG) {
        console.log('✅ Attaching smart login to form');
      }

      // Attach the smart login handler
      attachSmartLogin(form, emailInput, passwordInput);
    });
  }

  function attachSmartLogin(form, emailInput, passwordInput) {
    // Flag to prevent double submission
    let isProcessing = false;
    let activationNeeded = false; // Track if activation modal is shown

    // Use CAPTURE phase (true) to run before any other handlers
    form.addEventListener('submit', async function (e) {
      // ALWAYS prevent default first - we control submission
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation(); // Stop other handlers from running
      
      if (DEBUG) {
        console.log('⛔ Form submission blocked by smart login');
      }
      
      if (isProcessing) {
        return;
      }

      // If activation modal is already shown, don't process again
      if (activationNeeded) {
        console.log('⚠️ Activation needed - form submission blocked');
        return;
      }

      const email = emailInput.value.trim();
      const password = passwordInput.value;

      // Basic validation
      if (!email || !password) {
        // Let Shopify handle validation - re-submit without our handler
        isProcessing = false;
        form.removeEventListener('submit', arguments.callee);
        form.submit();
        return;
      }

      isProcessing = true;

      if (DEBUG) {
        console.log('🔒 Intercepted login submit for:', email);
      }

      // Show loading state on submit button
      const submitButton = form.querySelector('button[type="submit"]');
      const originalButtonText = submitButton ? submitButton.textContent : '';
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.textContent = 'Checking...';
      }

      try {
        // Call precheck API
        const response = await fetch(PRECHECK_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email }),
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();

        if (DEBUG) {
          console.log('📨 Precheck response:', data);
        }

        // Handle response
        if (data.action === 'PASS_THROUGH') {
          // Allow normal Shopify login to proceed
          if (DEBUG) console.log('➡️ PASS_THROUGH - storing token then submitting form');
          
          // Restore button
          if (submitButton) {
            submitButton.textContent = originalButtonText;
            submitButton.disabled = false;
          }

          // Create Storefront API token BEFORE submit so account page doesn't redirect to login
          await createAndStoreStorefrontToken(email, password);
          isProcessing = false;
          form.submit();
          
        } else if (data.action === 'ACTIVATION_SENT') {
          // Show activation modal - DO NOT SUBMIT FORM!
          if (DEBUG) console.log('📧 ACTIVATION_SENT - showing modal');
          
          // Set flag to prevent any further submissions
          activationNeeded = true;
          
          // Keep button disabled while modal is shown
          if (submitButton) {
            submitButton.textContent = 'Check Your Email';
            submitButton.disabled = true;
          }

          // DO NOT reset isProcessing - keep form locked
          // This prevents ANY form submission

          // Show the modal
          if (window.AuthActivationModal) {
            window.AuthActivationModal.show(
              email,
              data.cooldownSeconds || 0,
              data.alreadySent || false
            );
          } else {
            console.error('❌ AuthActivationModal not found');
            alert(
              'Your account needs activation. Please check your email for an activation link. ' +
              'If you don\'t see it, check your spam folder.'
            );
          }

          // IMPORTANT: Do NOT call form.submit() here!
          // Do NOT reset isProcessing
          // Form is now permanently blocked until page reload
          
        } else {
          // Unknown action - proceed with normal login
          if (DEBUG) console.log('⚠️ Unknown action - proceeding normally');
          
          if (submitButton) {
            submitButton.textContent = originalButtonText;
            submitButton.disabled = false;
          }

          await createAndStoreStorefrontToken(email, password);
          isProcessing = false;
          form.submit();
        }

      } catch (error) {
        console.error('❌ Smart login error:', error);
        console.error('API URL:', PRECHECK_API_URL);
        console.error('Error details:', error.message, error.stack);
        
        // On error, proceed with normal login flow
        if (DEBUG) {
          const errorMsg = `Precheck failed: ${error.message}\n\nAPI: ${PRECHECK_API_URL}\n\nProceeding with normal login`;
          alert(errorMsg);
          console.log('⚠️ Falling back to normal Shopify login');
        }

        if (submitButton) {
          submitButton.textContent = originalButtonText;
          submitButton.disabled = false;
        }

        isProcessing = false;
        form.submit();
      }
    }, true); // Use capture phase to run before other handlers
  }

  // Debug helper
  if (DEBUG) {
    window.SmartLogin = {
      test: function (email) {
        console.log('🧪 Testing precheck for:', email);
        fetch(PRECHECK_API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email }),
        })
          .then((r) => r.json())
          .then((d) => console.log('Response:', d))
          .catch((e) => console.error('Error:', e));
      },
    };
    console.log('💡 Debug mode enabled. Use SmartLogin.test("email@example.com") to test.');
  }
})();
